import React from 'react';
import { SITE_CONFIG, PAGE_SEO, personSchema, organizationSchema, websiteSchema, generateArticleSchema, KEYWORDS } from '../utils/seo';

// SEO Head Component for injecting meta tags
const SEOHead = ({ page = 'home', customData = {} }) => {
  const pageData = PAGE_SEO[page] || PAGE_SEO.home;
  const seoData = { ...pageData, ...customData };
  
  React.useEffect(() => {
    // Update document title
    document.title = seoData.title;
    
    // Update or create meta tags
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Standard meta tags
    updateMeta('description', seoData.description);
    updateMeta('keywords', seoData.keywords || KEYWORDS.join(', '));
    updateMeta('author', 'Ruben P. Wargner');
    updateMeta('robots', 'index, follow');
    
    // OpenGraph tags
    updateMeta('og:title', seoData.title, true);
    updateMeta('og:description', seoData.description, true);
    updateMeta('og:image', seoData.heroImage || SITE_CONFIG.defaultImage, true);
    updateMeta('og:url', `${SITE_CONFIG.siteUrl}${seoData.path || ''}`, true);
    updateMeta('og:type', seoData.ogType || 'website', true);
    updateMeta('og:site_name', SITE_CONFIG.siteName, true);
    
    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', seoData.title);
    updateMeta('twitter:description', seoData.description);
    updateMeta('twitter:image', seoData.heroImage || SITE_CONFIG.defaultImage);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_CONFIG.siteUrl}${seoData.path || ''}`);
    
  }, [seoData]);
  
  return null;
};

// JSON-LD Script Component
const JsonLd = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

// Home Page SEO Component
export const HomePageSEO = () => {
  React.useEffect(() => {
    // Inject JSON-LD scripts
    const schemas = [personSchema, organizationSchema, websiteSchema];
    
    // Remove existing JSON-LD scripts
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    
    // Add new JSON-LD scripts
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    
    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    };
  }, []);
  
  return <SEOHead page="home" customData={{ path: '' }} />;
};

// Article Page SEO Component
export const ArticlePageSEO = ({ articleKey, path }) => {
  const pageData = PAGE_SEO[articleKey];
  
  React.useEffect(() => {
    if (!pageData) return;
    
    // Remove existing JSON-LD scripts
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    
    // Generate article schema
    const articleSchema = generateArticleSchema({
      title: pageData.title.split(' | ')[0],
      description: pageData.description,
      heroImage: pageData.heroImage,
      datePublished: pageData.datePublished,
      section: pageData.section,
      path: path
    });
    
    // Add schemas
    const schemas = [articleSchema, personSchema, organizationSchema];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    
    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    };
  }, [pageData, path]);
  
  return <SEOHead page={articleKey} customData={{ path }} />;
};

export { SEOHead, JsonLd };
export default SEOHead;
